import { is_driver_flow_path } from './driver-paths'

export const CLIENT_ENTRY_PATH = '/empresarial'

export function clean_client_path(path: string): string {
  const withoutQuery = String(path || '').split('?')[0] ?? ''
  const clean = (withoutQuery.split('#')[0] ?? '') || '/'
  return clean === '/' ? clean : clean.replace(/\/+$/, '') || '/'
}

export function is_client_entry_path(path: string): boolean {
  return clean_client_path(path) === CLIENT_ENTRY_PATH
}

export function is_client_flow_path(path: string): boolean {
  const clean = clean_client_path(path)
  return clean === CLIENT_ENTRY_PATH
    || clean === '/combustiveis'
    || clean === '/avaliacoes'
    || clean === '/voucher'
    || clean === '/pagamento'
    || clean.startsWith('/pagamento/')
}

export function parse_cnpj_from_search(search: string): string | null {
  if (!search || search === '?') return null
  const raw = search.startsWith('?') ? search : `?${search}`
  const params = new URLSearchParams(raw)
  for (const [key, value] of params.entries()) {
    const normalized_key = key.toLowerCase()
    if (normalized_key !== 'cnpj' && normalized_key !== 'cnpj_empresa') continue
    const digits = value.replace(/\D/g, '')
    if (digits.length === 14) return digits
  }

  const first_segment = raw.slice(1).split('&')[0] ?? ''
  if (!first_segment || first_segment.includes('=')) return null
  let decoded = first_segment
  try {
    decoded = decodeURIComponent(first_segment.replace(/\+/g, ' '))
  } catch {
    // URL malformada não deve interromper a inicialização; valida os dígitos crus.
  }
  const digits = decoded.replace(/\D/g, '')
  return digits.length === 14 ? digits : null
}

export function canonical_client_search(search: string): string {
  if (!search || search === '?') return search
  const raw = search.startsWith('?') ? search.slice(1) : search
  const segments = raw.split('&')
  const first_segment = segments[0] ?? ''
  const legacy_cnpj = first_segment && !first_segment.includes('=')
    ? parse_cnpj_from_search(`?${first_segment}`)
    : null
  const params = new URLSearchParams(legacy_cnpj ? segments.slice(1).join('&') : raw)
  let named_cnpj: string | null = null

  for (const [key, value] of Array.from(params.entries())) {
    const normalized_key = key.toLowerCase()
    if (normalized_key !== 'cnpj' && normalized_key !== 'cnpj_empresa') continue
    if (!named_cnpj) {
      const digits = value.replace(/\D/g, '')
      if (digits.length === 14) named_cnpj = digits
    }
    params.delete(key)
  }

  const cnpj = named_cnpj || legacy_cnpj
  const needs_canonical_form = !!named_cnpj || (!!legacy_cnpj && segments.length > 1)
  if (!cnpj || !needs_canonical_form) return search.startsWith('?') ? `?${raw}` : raw

  const canonical = new URLSearchParams()
  canonical.set('cnpj', cnpj)
  for (const [key, value] of params.entries()) canonical.append(key, value)
  const query = canonical.toString()
  return search.startsWith('?') ? `?${query}` : query
}

export function canonical_client_path(path: string): string {
  const raw = String(path || '')
  const hash_index = raw.indexOf('#')
  const hash = hash_index === -1 ? '' : raw.slice(hash_index)
  const without_hash = hash_index === -1 ? raw : raw.slice(0, hash_index)
  const query_index = without_hash.indexOf('?')
  const pathname = clean_client_path(query_index === -1 ? without_hash : without_hash.slice(0, query_index))
  if (query_index === -1) return `${pathname}${hash}`
  const search = canonical_client_search(without_hash.slice(query_index))
  return `${pathname}${search}${hash}`
}

export function parse_cnpj_from_path(path: string): string | null {
  const raw = String(path || '')
  const query_index = raw.indexOf('?')
  if (query_index === -1) return null
  const hash_index = raw.indexOf('#', query_index)
  const search = hash_index === -1 ? raw.slice(query_index) : raw.slice(query_index, hash_index)
  return parse_cnpj_from_search(search)
}

export function driver_return_path_from_search(search: string): string | null {
  const raw = new URLSearchParams(search).get('voltar')
  const clean = raw ? route_without_pin(raw) : null
  return clean && is_driver_flow_path(clean) ? clean : null
}

export function client_driver_return_suffix(search: string): string {
  const returnPath = driver_return_path_from_search(search)
  if (!returnPath) return ''
  const params = new URLSearchParams({ voltar: returnPath })
  return `?${params.toString()}`
}

export function client_scope_suffix(cnpj: string, search: string): string {
  const digits = String(cnpj || '').replace(/\D/g, '')
  const params = new URLSearchParams()
  if (digits.length === 14) params.set('cnpj', digits)
  const returnPath = driver_return_path_from_search(search)
  if (returnPath) params.set('voltar', returnPath)
  const query = params.toString()
  return query ? `?${query}` : ''
}

function route_without_pin(raw_path: string): string {
  const raw = String(raw_path || '')
  const hash_index = raw.indexOf('#')
  const hash = hash_index === -1 ? '' : raw.slice(hash_index)
  const without_hash = hash_index === -1 ? raw : raw.slice(0, hash_index)
  const query_index = without_hash.indexOf('?')
  if (query_index === -1) return raw

  const path = without_hash.slice(0, query_index) || '/'
  const params = new URLSearchParams(without_hash.slice(query_index))
  if (!params.has('pin')) return raw
  params.delete('pin')
  const query = params.toString()
  return `${path}${query ? `?${query}` : ''}${hash}`
}
